import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Package, 
  Plus, 
  Pencil, 
  Trash2, 
  Save,
  X,
  Image as ImageIcon,
  Loader2,
  Database
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  "Colour Cosmetics",
  "Skin Care",
  "Hair Care",
  "Body Care"
];

const ProductManagement = ({ getAuthHeaders, onUnauthorized }) => {
  const { toast } = useToast();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  
  // Dialog states
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    description: '',
    image: '',
    shadeCount: '',
    altText: ''
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/products?active_only=false`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast({
        title: "Error",
        description: "Failed to fetch products",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const seedProducts = async () => {
    try {
      setSeeding(true);
      const headers = getAuthHeaders();
      const response = await axios.post(`${API}/products/seed`, {}, { headers });
      
      toast({
        title: "Success",
        description: response.data.message,
      });
      
      if (response.data.seeded > 0) {
        fetchProducts();
      }
    } catch (error) {
      console.error('Error seeding products:', error);
      if (error.response?.status === 401) {
        onUnauthorized();
      } else {
        toast({
          title: "Error",
          description: "Failed to seed products",
          variant: "destructive"
        });
      }
    } finally {
      setSeeding(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      category: '',
      description: '',
      image: '',
      shadeCount: '',
      altText: ''
    });
  };

  const openAddDialog = () => {
    resetForm();
    setIsAddDialogOpen(true);
  };

  const openEditDialog = (product) => {
    setSelectedProduct(product);
    setFormData({
      name: product.name || '',
      category: product.category || '',
      description: product.description || '',
      image: product.image || '',
      shadeCount: product.shadeCount || '',
      altText: product.altText || ''
    });
    setIsEditDialogOpen(true);
  };

  const openDeleteDialog = (product) => {
    setSelectedProduct(product);
    setIsDeleteDialogOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCategoryChange = (value) => {
    setFormData(prev => ({ ...prev, category: value }));
  };

  const handleAddProduct = async () => {
    if (!formData.name || !formData.category || !formData.image) {
      toast({
        title: "Validation Error",
        description: "Name, category, and image URL are required",
        variant: "destructive"
      });
      return;
    }

    try {
      setSaving(true);
      const headers = getAuthHeaders();
      
      await axios.post(`${API}/products`, formData, { headers });
      
      toast({
        title: "Success",
        description: "Product added successfully",
      });
      
      setIsAddDialogOpen(false);
      resetForm();
      fetchProducts();
    } catch (error) {
      console.error('Error adding product:', error);
      if (error.response?.status === 401) {
        onUnauthorized();
      } else {
        toast({
          title: "Error",
          description: "Failed to add product",
          variant: "destructive"
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateProduct = async () => {
    if (!formData.name || !formData.category || !formData.image) {
      toast({
        title: "Validation Error",
        description: "Name, category, and image URL are required",
        variant: "destructive"
      });
      return;
    }

    try {
      setSaving(true);
      const headers = getAuthHeaders();
      
      await axios.put(`${API}/products/${selectedProduct.id}`, formData, { headers });
      
      toast({
        title: "Success",
        description: "Product updated successfully",
      });
      
      setIsEditDialogOpen(false);
      setSelectedProduct(null);
      resetForm();
      fetchProducts();
    } catch (error) {
      console.error('Error updating product:', error);
      if (error.response?.status === 401) {
        onUnauthorized();
      } else {
        toast({
          title: "Error",
          description: "Failed to update product",
          variant: "destructive"
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProduct = async () => {
    try {
      setSaving(true);
      const headers = getAuthHeaders();
      
      await axios.delete(`${API}/products/${selectedProduct.id}`, { headers });
      
      toast({
        title: "Success",
        description: "Product deleted successfully",
      });
      
      setIsDeleteDialogOpen(false);
      setSelectedProduct(null);
      fetchProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      if (error.response?.status === 401) {
        onUnauthorized();
      } else {
        toast({
          title: "Error",
          description: "Failed to delete product",
          variant: "destructive"
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const ProductForm = ({ onSubmit, submitLabel }) => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="name">Product Name *</Label>
          <Input
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            placeholder="e.g., Lip Gloss"
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor="category">Category *</Label>
          <Select value={formData.category} onValueChange={handleCategoryChange}>
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent>
              {CATEGORIES.map(cat => (
                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div>
        <Label htmlFor="image">Image URL *</Label>
        <Input
          id="image"
          name="image"
          value={formData.image}
          onChange={handleInputChange}
          placeholder="https://images.unsplash.com/..."
          className="mt-1"
        />
        {formData.image && (
          <div className="mt-2 relative w-32 h-32 rounded-lg overflow-hidden border">
            <img 
              src={formData.image} 
              alt="Preview" 
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>
        )}
      </div>
      
      <div>
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          name="description"
          value={formData.description}
          onChange={handleInputChange}
          placeholder="Brief product description..."
          rows={2}
          className="mt-1"
        />
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="shadeCount">Shade Count</Label>
          <Input
            id="shadeCount"
            name="shadeCount"
            value={formData.shadeCount}
            onChange={handleInputChange}
            placeholder="e.g., 6 Shades"
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor="altText">SEO Alt Text</Label>
          <Input
            id="altText"
            name="altText"
            value={formData.altText}
            onChange={handleInputChange}
            placeholder="SEO description for image"
            className="mt-1"
          />
        </div>
      </div>
      
      <DialogFooter>
        <Button variant="outline" onClick={() => {
          setIsAddDialogOpen(false);
          setIsEditDialogOpen(false);
        }}>
          Cancel
        </Button>
        <Button onClick={onSubmit} disabled={saving}>
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              {submitLabel}
            </>
          )}
        </Button>
      </DialogFooter>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header Actions */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Product Catalogue</h2>
          <p className="text-sm text-gray-600">{products.length} products</p>
        </div>
        <div className="flex gap-2">
          {products.length === 0 && (
            <Button variant="outline" onClick={seedProducts} disabled={seeding}>
              {seeding ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Seeding...
                </>
              ) : (
                <>
                  <Database className="w-4 h-4 mr-2" />
                  Seed Initial Products
                </>
              )}
            </Button>
          )}
          <Button onClick={openAddDialog}>
            <Plus className="w-4 h-4 mr-2" />
            Add Product
          </Button>
        </div>
      </div>

      {/* Products Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto" />
              <p className="mt-4 text-gray-600">Loading products...</p>
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-12">
              <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-4">No products found</p>
              <Button onClick={seedProducts} disabled={seeding}>
                <Database className="w-4 h-4 mr-2" />
                Seed Initial Products
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16">Image</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Shades</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {products.map((product) => (
                    <TableRow key={product.id}>
                      <TableCell>
                        <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100">
                          <img 
                            src={product.image} 
                            alt={product.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.src = 'https://via.placeholder.com/48?text=No+Image';
                            }}
                          />
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">{product.name}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{product.category}</Badge>
                      </TableCell>
                      <TableCell className="text-gray-600">
                        {product.shadeCount || '-'}
                      </TableCell>
                      <TableCell>
                        <Badge className={product.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                          {product.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEditDialog(product)}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:bg-red-50"
                            onClick={() => openDeleteDialog(product)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Product Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add New Product</DialogTitle>
            <DialogDescription>
              Add a new product to your catalogue. Fill in the details below.
            </DialogDescription>
          </DialogHeader>
          <ProductForm onSubmit={handleAddProduct} submitLabel="Add Product" />
        </DialogContent>
      </Dialog>

      {/* Edit Product Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Product</DialogTitle>
            <DialogDescription>
              Update the product details below.
            </DialogDescription>
          </DialogHeader>
          <ProductForm onSubmit={handleUpdateProduct} submitLabel="Save Changes" />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Product</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{selectedProduct?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteProduct}
              disabled={saving}
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductManagement;
